import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loadPostCollectionAsync } from '../../data-layer/posts/actions';
import { PostModel } from '../../data-layer/posts/types';
import { BusinessPostStub } from '../../ui/business/BusinessPostStub/BusinessPostStub';
import { PostCardCompact } from '../../ui/posts/PostCardCompact';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { analyticsService, EventSource } from '../../utils/analyticsService';
import {
    ymTrackPostInGridClick,
    ymTrackPostKebabClick,
    ymTrackProfilePostClick,
} from '../../utils/metrika';
import { useExperimentFlag } from '../../utils/useExperimentFlag';
import { VirtualGrid } from '../post/VirtualGrid';
import { usePostCollection } from '../post/usePostCollection';
import { withPostCallbacks } from '../post/withPostCallbacks';
import { useCurrentUser } from '../users/useCurrentUser';
import { useItemsWithoutAd } from '../utils/useItemsWithAd';
import { BusinessResizableGrid } from './BusinessResizableGrid/BusinessResizableGrid';
import { useBusinessModel } from './useBusinessModel';

const PER_PAGE = 24;
const COLUMNS = 3;

const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.BUSINESS);

type Filters = Omit<Parameters<typeof loadPostCollectionAsync>[1], 'next'>;

export const makeBusinessPostsTab = (namespace: string, filters: Partial<Filters>) => () => {
    const params = useParams();
    const businessId = params.businessId!;
    const business = useBusinessModel({ businessId });
    const navigate = useNavigate();
    const postCollection = usePostCollection({
        namespace: `${namespace}-${businessId}`,
        filters: {
            ...filters,
            businessId,
            amount: PER_PAGE,
        },
        forceFiltersUpdate: true,
    });
    const currentUser = useCurrentUser();
    const onPostCardClick = useCallback(
        (
            id: PostModel['id'],
            postNumber: number,
            postUrl: PostModel['url'],
            postBusinessId: string | undefined,
            logContext: PostModel['logContext'],
            djRequestId: PostModel['djRequestId'],
        ) => {
            const goalInfo = {
                page: 'Профиль бизнеса',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            };
            ymTrackProfilePostClick(goalInfo);
            ymTrackPostInGridClick(goalInfo);
            analyticsService.trackGridTap({
                eventSource: EventSource.BUSINESS,
                postId: id,
                postNumber,
                businessId: postBusinessId,
                logContext,
                djRequestId,
            });

            // Прокидываем id сущности, из которой мы пришли в карточку поста
            // это нужно для аналитики
            navigate(postUrl, {
                state: {
                    fromBusinessId: businessId,
                },
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentUser?.login, currentUser?.profile?.publicId, businessId],
    );

    const onPostKebabClick = useCallback(() => {
        ymTrackPostKebabClick({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    const itemsWithoutAds = useItemsWithoutAd({
        items: postCollection.items,
    });

    const enableDiscoveryGrid = useExperimentFlag('discovery_grid_profile');

    if (!itemsWithoutAds.length && !postCollection.isLoading) {
        return <BusinessPostStub business={business} currentUser={currentUser} />;
    }

    if (enableDiscoveryGrid) {
        return (
            <BusinessResizableGrid
                items={itemsWithoutAds}
                businessId={businessId}
                onLoadNext={postCollection.onLoadNext}
                user={currentUser}
                onPostKebabClick={onPostKebabClick}
                isService={business?.isService}
            />
        );
    }

    return (
        <VirtualGrid
            items={itemsWithoutAds}
            isEndReached={postCollection.isEndReached}
            onLoadNext={postCollection.onLoadNext}
            columns={COLUMNS}
            renderItem={(postModel, index) => (
                <PostCardCompact
                    key={postModel.id}
                    post={postModel}
                    index={index}
                    onPostCardClick={onPostCardClick}
                    renderMenu={(trigger) =>
                        postModel.isAd ? null : (
                            <EnhancedPostContextMenu
                                index={index}
                                trigger={trigger}
                                onTriggerClick={onPostKebabClick}
                                post={postModel}
                                user={currentUser}
                                fromBusinessId={businessId}
                            />
                        )
                    }
                />
            )}
        />
    );
};
